import { cn } from "@/lib/utils";

/* ── Card variants ──────────────────────────────────────────── */

interface CardProps extends React.ComponentProps<"div"> {
  variant?: "default" | "outlined" | "elevated";
  /** Enable hover/focus affordances for clickable cards (wrapped in Link or with onClick) */
  interactive?: boolean;
}

function Card({ className, variant = "default", interactive = false, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(
        "flex flex-col gap-[var(--space-4)] overflow-hidden rounded-[var(--radius-xl)] text-[length:var(--font-size-sm)]",
        "transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]",
        variant === "default" && [
          "bg-[var(--color-bg-card)] text-[var(--color-text-primary)]",
          "border border-[var(--color-border-default)]",
        ],
        variant === "outlined" && [
          "bg-transparent text-[var(--color-text-primary)]",
          "border border-[var(--color-border-default)]",
        ],
        variant === "elevated" && [
          "bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]",
          "border border-[var(--color-border-default)]",
          "shadow-[var(--shadow-sm)]",
        ],
        interactive && [
          "cursor-pointer",
          "hover:border-[var(--color-border-strong)] hover:shadow-[var(--shadow-md)]",
          variant === "elevated" && "hover:shadow-[var(--shadow-lg)] hover:-translate-y-px",
        ],
        className
      )}
      {...props}
    />
  );
}

/* ── Card subcomponents ─────────────────────────────────────── */

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "flex flex-col gap-[var(--space-1)] px-[var(--space-6)] pt-[var(--space-6)]",
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"h3">) {
  return (
    <h3
      data-slot="card-title"
      className={cn(
        "font-heading text-[length:var(--font-size-lg)] leading-[var(--line-height-tight)] font-[number:var(--font-weight-bold)] tracking-[var(--letter-spacing-tight)]",
        className
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="card-description"
      className={cn(
        "text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]",
        className
      )}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("ml-auto self-start", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-[var(--space-6)]", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center gap-[var(--space-3)] border-t border-[var(--color-border-subtle)]",
        "bg-[var(--color-bg-muted)] px-[var(--space-6)] py-[var(--space-5)]",
        className
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
export type { CardProps };
